import { Alert } from '@mm/components';
import { landmineNames } from '@mm/constants/landmine-names';
import { SmsKeywordIcon } from '@mm/icons/StarterIcons';
import subscriptionCRUDActions from '@mm/state/actions/SubscriptionCRUDActions';
import twilioCRUDActions from '@mm/state/actions/TwilioCRUDActions';
import { UserRole } from '@mm/state/model/CompanyUsersModel';
import { isBilling2023Subscription } from '@mm/state/model/SubscriptionModel';
import modalActions from '@mm/state/modules/modalsState/actions';
import {
  activeFacebookPagePlanInfoSelector,
  activeFacebookPageSelector,
} from '@mm/state/selectors/facebookPageSelector';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import { getCurrentSubscription } from '@mm/state/selectors/subscriptionSelectors';
import {
  hasDeactivatedNumberSelector,
  recentDiscardedSelector,
} from '@mm/state/selectors/twillio';
import {
  activeChatbotSelector,
  activeFacebookPageIdSelector,
} from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { get } from 'lodash';
import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useRouter from 'use-react-router';
import { PrimaryButton } from '../../../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { GreenMarker } from '../../../../../../../../common/components/GreenMarker/GreenMarker';
import { useGreenCueMarkers } from '../../../../../../../../common/hooks/use-green-cue-markers';
import { BotBuilderRoutes } from '../../../../../../../../routes/bot-builder-routes';
import { SMSCheckArea } from '../SMSCheckArea/SMSCheckArea';
import './ActivateNumber.pcss';
import IphoneIcon from './IphoneIcon.png';

export const ActivateNumber = () => {
  const dispatch = useDispatch();
  const { history, location } = useRouter();
  const { id } = useSelector(activeChatbotSelector);
  const facebookPage = useSelector(activeFacebookPageSelector);
  const activeFacebookPagePlanInfo = useSelector(
    activeFacebookPagePlanInfoSelector,
  ).info;
  const hasDeactivatedNumber = useSelector(hasDeactivatedNumberSelector);
  const facebookPageId = useSelector(activeFacebookPageIdSelector);
  const recentDiscardedNumber = useSelector(recentDiscardedSelector);
  const users = useSelector((state: StoreState) => state.companyUsers.entities);
  const userId = useSelector((state: StoreState) => state.session.session.id);
  const navigatedFrom = get(location, 'state.navigatedFrom', null);
  const hasActivateNumberFlag = useSelector(
    combinedFeatureFlagsSelector,
  ).smsActivateNumber;
  const isNewBillingSubscription =
    facebookPage?.subscription &&
    isBilling2023Subscription(facebookPage?.subscription);

  useEffect(() => {
    dispatch(
      subscriptionCRUDActions.getCurrentSubscription(
        facebookPageId,
        false,
        facebookPage?.subscription?.id,
        isNewBillingSubscription,
      ),
    );
  }, [dispatch, facebookPageId]);

  const { isSmsActivated } = useGreenCueMarkers();

  const currentUserRole = useMemo(
    () => users.find(user => user.id === userId)?.role || '',
    [userId, users],
  );
  const subscription = useSelector(getCurrentSubscription(facebookPageId));
  const disableSMSActivateButton = useMemo(
    () =>
      activeFacebookPagePlanInfo.isNewTeamPlan ||
      activeFacebookPagePlanInfo.isLegacyProPlan ||
      (activeFacebookPagePlanInfo.isPromoPlan &&
        !facebookPage?.redemption?.freeTrial) ||
      activeFacebookPagePlanInfo.isLegacyTeamPlan ||
      activeFacebookPagePlanInfo.isSpecialPro,
    [
      activeFacebookPagePlanInfo.isLegacyProPlan,
      activeFacebookPagePlanInfo.isLegacyTeamPlan,
      activeFacebookPagePlanInfo.isNewTeamPlan,
      activeFacebookPagePlanInfo.isPromoPlan,
      activeFacebookPagePlanInfo.isSpecialPro,
      facebookPage?.redemption?.freeTrial,
    ],
  );

  const isSubscriptionCanceled =
    subscription?.canceledAt && subscription?.state !== 'active';
  // ToDo: why do we have this check for specific plan type?
  const isActiveBySubscription =
    subscription?.planCode === 'team_custom_12month299';
  const disableActivateSMSButtonByRole = useMemo(
    () =>
      [UserRole.Collaborator, UserRole.FacebookPageCollaborator].includes(
        currentUserRole as UserRole,
      ), // conver string role from user instance to UserRole
    [currentUserRole],
  );

  const isActivateButtonDisabled =
    !isActiveBySubscription &&
    (disableSMSActivateButton ||
      activeFacebookPagePlanInfo.isSpecialTeam ||
      isSubscriptionCanceled);

  const disableActivateButton =
    isActivateButtonDisabled ||
    disableActivateSMSButtonByRole ||
    (hasDeactivatedNumber &&
      (isSubscriptionCanceled || activeFacebookPagePlanInfo.isFreePlan));

  const onClickActivate = async () => {
    if (hasDeactivatedNumber) {
      await dispatch(
        twilioCRUDActions.activatePage(facebookPageId, true, {
          numberKind: 'twilio',
          number: recentDiscardedNumber,
        }),
      );

      history.push({
        pathname: `${BotBuilderRoutes.settingsSmsNumber.getLink({
          chatbotId: Number(id),
        })}`,
        state: { allowRedirect: true, navigatedFrom },
      });

      return;
    }

    if (!hasActivateNumberFlag) {
      dispatch(
        modalActions.openUniversalUpgradeLandmineModal({
          feature: 'smsActivateNumber',
          landmineName: landmineNames['SMS-ACTIVATE-NUMBER'],
        }),
      );
      return;
    }

    history.push({
      pathname: `${BotBuilderRoutes.settingsSmsAreaCodeSearch.getLink({
        chatbotId: Number(id),
      })}`,
      state: { allowRedirect: true, navigatedFrom },
    });
  };

  const showAlertMessage =
    isSubscriptionCanceled ||
    (!isActiveBySubscription && disableSMSActivateButton);

  const getMessage = () => {
    if (isSubscriptionCanceled) {
      return (
        <div className="activate-number__warning-message">
          <span className="activate-number__warning-text">
            {' '}
            Page with a canceled subscription cannot activate number.
          </span>
        </div>
      );
    }

    if (!isActiveBySubscription && disableSMSActivateButton) {
      return (
        <div className="activate-number__warning-message">
          <span className="activate-number__warning-text">
            SMS is not available on{' '}
            {facebookPage?.redemption?.instachamp
              ? 'InstaChamp AppSumo Promotion.'
              : facebookPage?.subscription?.planCode.includes('instachamp')
              ? 'InstaChamp Platinum plan.'
              : 'this plan.'}
          </span>
          <span>
            &nbsp;Please{' '}
            <a
              className="activate-number__link"
              target="_self"
              rel="noopener noreferrer"
              href="/settings/billing"
            >
              upgrade
            </a>{' '}
            <span> to a Customers.ai OmniChat plan to activate SMS. </span>
          </span>
          <a
            className="activate-number__learn-more-link"
            href=" https://customers.ai/help/article/vmlmm7k6q0-why-can-t-i-use-sms-on-a-legacy-plan"
            target="_blank"
          >
            Learn More
          </a>
        </div>
      );
    }
  };

  return (
    <div>
      {showAlertMessage && (
        <Alert
          showInfoIcon
          className="activate-number__alert"
          showCloseIcon={false}
        >
          {getMessage()}
        </Alert>
      )}
      <div className="activate-number__wrapper">
        <div className="activate-number__column">
          <div className="activate-number__check-area">
            <SMSCheckArea>
              Get a phone number for sending SMS messages to your customers and
              prospects!
            </SMSCheckArea>

            <SMSCheckArea>
              Create <b>Keywords</b> to build an SMS opted-in list
            </SMSCheckArea>

            <SMSCheckArea>
              Create SMS Chatbots, Chat Blasts, and Drip Campaigns!
            </SMSCheckArea>
          </div>
          <div className="activate-number__button-area">
            <PrimaryButton
              id="ActivateButtonTooltip"
              leftIcon={
                <SmsKeywordIcon className="activate-number__button-area-icon" />
              }
              onClick={onClickActivate}
              disabled={disableActivateButton}
            >
              <span>
                {hasDeactivatedNumber
                  ? `Restore ${recentDiscardedNumber}`
                  : 'Activate SMS'}
              </span>
            </PrimaryButton>
            {!isSmsActivated && (
              <GreenMarker className="activate-number__button-area--green-marker" />
            )}
          </div>
        </div>
        <div className="activate-number__column">
          <img src={IphoneIcon} className="activate-number__phone-icon" />
        </div>
      </div>
    </div>
  );
};
